import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Toaster, toast } from "sonner";
import { Crown, Lock, Plus, Trash2, Pencil, ArrowLeft, Power, Ban, X } from "lucide-react";
import { listAllShops, updateShop, deleteShop, createShop, bulkCreateServices, isShopSuspended } from "../lib/api";
import { hashPin, isHashed } from "../lib/pinSecurity";

const SUPER_PIN = process.env.REACT_APP_SUPER_ADMIN_PIN || "9999";
const PLANS = ["free", "economy", "premium"];
const PAY_STATUS = ["paid", "due", "expired"];

export default function SuperAdminPage() {
  const [authed, setAuthed] = useState(false);
  const [pin, setPin] = useState("");
  const [error, setError] = useState(false);

  function submitPin() {
    if (pin === SUPER_PIN) { setAuthed(true); setError(false); }
    else { setError(true); setPin(""); }
  }

  if (!authed) {
    return (
      <div className="min-h-screen bg-void text-white flex items-center justify-center p-6">
        <div className="glass-strong rounded-2xl p-8 max-w-sm w-full">
          <div className="flex items-center gap-2 text-gold font-numbers text-[10px] tracking-[0.4em] uppercase">
            <Crown className="w-3.5 h-3.5" /> Super Admin
          </div>
          <h1 className="font-heading text-2xl mt-2">Restricted Access</h1>
          <input
            type="password" value={pin}
            onChange={(e) => { setPin(e.target.value.replace(/\D/g, "").slice(0, 6)); setError(false); }}
            onKeyDown={(e) => e.key === "Enter" && submitPin()}
            placeholder="Enter Super Admin PIN"
            className="input-minimal mt-6 font-numbers tracking-[0.4em] text-center"
            data-testid="super-pin"
            autoFocus
          />
          {error && <div className="text-red-400 text-xs mt-3" data-testid="super-pin-error">Incorrect PIN</div>}
          <button onClick={submitPin} className="btn-gold w-full mt-6" data-testid="super-pin-submit">
            <Lock className="w-4 h-4 inline mr-2" /> Unlock
          </button>
          <Link to="/" className="block text-center text-xs text-white/40 hover:text-gold mt-4">← Home</Link>
        </div>
      </div>
    );
  }

  return <SuperConsole />;
}

function SuperConsole() {
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  async function refresh() {
    setLoading(true);
    try { setShops(await listAllShops()); }
    catch (e) { toast.error(e.message + " — apply supabase_phase3.sql"); }
    finally { setLoading(false); }
  }
  useEffect(() => { refresh(); }, []);

  const stats = useMemo(() => {
    const total = shops.length;
    const active = shops.filter((s) => s.active !== false && !s.blocked && !isShopSuspended(s)).length;
    const expired = shops.filter((s) => isShopSuspended(s) && isShopSuspended(s).reason === "expired").length;
    const due = shops.filter((s) => s.payment_status === "due").length;
    return { total, active, expired, due };
  }, [shops]);

  return (
    <div className="min-h-screen bg-void text-white">
      <Toaster theme="dark" position="top-center" richColors />
      <header className="px-5 md:px-10 py-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full border border-gold/40 flex items-center justify-center gold-glow">
            <Crown className="w-4 h-4 text-gold" />
          </div>
          <div>
            <div className="font-heading text-lg">AIBox Super Admin</div>
            <div className="font-numbers text-[10px] tracking-[0.3em] uppercase text-white/40">Multi-Tenant Control</div>
          </div>
        </div>
        <Link to="/" className="text-white/50 hover:text-gold text-sm inline-flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Home
        </Link>
      </header>

      <main className="px-5 md:px-10 pb-16 max-w-7xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Kpi label="Total Shops" value={stats.total} />
          <Kpi label="Active" value={stats.active} accent />
          <Kpi label="Expired" value={stats.expired} />
          <Kpi label="Due Payments" value={stats.due} />
        </div>

        <div className="mt-8 flex items-center justify-between">
          <div className="font-numbers text-[10px] tracking-[0.4em] uppercase text-white/40">All Shops</div>
          <button onClick={() => setCreating(true)} className="btn-gold text-sm py-2 px-4 inline-flex items-center gap-2" data-testid="super-add-shop">
            <Plus className="w-4 h-4" /> Add Shop
          </button>
        </div>

        <div className="mt-4 rounded-2xl bg-charcoal border border-white/10 overflow-hidden">
          {loading ? <div className="p-12 text-center text-white/40 text-sm">Loading…</div>
            : shops.length === 0 ? <div className="p-12 text-center text-white/40 text-sm">No shops yet.</div>
            : <div className="overflow-x-auto"><table className="w-full text-sm">
                <thead><tr className="text-left text-[10px] font-numbers tracking-[0.3em] uppercase text-white/40 border-b border-white/10">
                  <th className="px-4 py-3">Shop</th><th className="px-4 py-3">Plan</th><th className="px-4 py-3">Payment</th>
                  <th className="px-4 py-3">Expiry</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th>
                </tr></thead>
                <tbody data-testid="super-shops-table">
                  {shops.map((s) => {
                    const susp = isShopSuspended(s);
                    return (
                    <tr key={s.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="px-4 py-3">
                        <div className="font-body">{s.name}</div>
                        <div className="text-[10px] font-numbers tracking-[0.25em] uppercase text-white/40">/s/{s.slug}</div>
                      </td>
                      <td className="px-4 py-3"><Pill v={s.plan || "free"} tone="gold" /></td>
                      <td className="px-4 py-3"><Pill v={s.payment_status || "paid"} tone={s.payment_status === "paid" ? "ok" : s.payment_status === "due" ? "warn" : "bad"} /></td>
                      <td className="px-4 py-3 font-numbers text-xs text-white/60">{s.expiry_date || "—"}</td>
                      <td className="px-4 py-3">{susp ? <Pill v={susp.reason} tone="bad" /> : <Pill v="live" tone="ok" />}</td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => setEditing(s)} className="p-2 text-white/60 hover:text-gold" data-testid={`super-edit-${s.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                        <button onClick={async () => {
                          await updateShop(s.id, { blocked: !s.blocked });
                          toast.success(s.blocked ? "Unblocked" : "Blocked"); refresh();
                        }} className="p-2 text-white/60 hover:text-red-400" data-testid={`super-block-${s.id}`}><Ban className="w-3.5 h-3.5" /></button>
                        <button onClick={async () => {
                          await updateShop(s.id, { active: !(s.active !== false) });
                          toast.success("Toggled"); refresh();
                        }} className="p-2 text-white/60 hover:text-gold" data-testid={`super-active-${s.id}`}><Power className="w-3.5 h-3.5" /></button>
                        <button onClick={async () => {
                          if (!window.confirm(`Delete shop "${s.name}" and ALL its data?`)) return;
                          await deleteShop(s.id); toast.success("Deleted"); refresh();
                        }} className="p-2 text-white/60 hover:text-red-400" data-testid={`super-delete-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                      </td>
                    </tr>);
                  })}
                </tbody>
              </table></div>}
        </div>
      </main>

      {(editing || creating) && <ShopEditor shop={editing} isNew={creating} onClose={() => { setEditing(null); setCreating(false); refresh(); }} />}
    </div>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <div className="rounded-2xl bg-charcoal border border-white/10 p-5">
      <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 font-numbers">{label}</div>
      <div className={`mt-3 font-numbers text-3xl tabular-nums ${accent ? "text-gold" : "text-white"}`}>{value}</div>
    </div>
  );
}

function Pill({ v, tone = "gold" }) {
  const map = { gold: "border-gold/40 text-gold", ok: "border-emerald-400/40 text-emerald-400", warn: "border-amber-400/40 text-amber-400", bad: "border-red-400/40 text-red-400" };
  return <span className={`inline-block text-[10px] font-numbers tracking-[0.25em] uppercase border rounded-full px-2 py-0.5 ${map[tone]}`}>{v}</span>;
}

function ShopEditor({ shop, isNew, onClose }) {
  const [name, setName] = useState(shop?.name || "");
  const [slug, setSlug] = useState(shop?.slug || "");
  // If the stored PIN is already hashed, leave the field blank — admin types a new one only if they want to reset.
  const [pin, setPin] = useState(shop?.admin_pin && !isHashed(shop.admin_pin) ? shop.admin_pin : "");
  const pinIsHashed = !!shop?.admin_pin && isHashed(shop.admin_pin);
  const [plan, setPlan] = useState(shop?.plan || "free");
  const [pay, setPay] = useState(shop?.payment_status || "paid");
  const [start, setStart] = useState(shop?.start_date || new Date().toISOString().slice(0, 10));
  const [expiry, setExpiry] = useState(shop?.expiry_date || "");
  const [notes, setNotes] = useState(shop?.notes || "");
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      // PIN handling:
      // - If user typed digits, hash them.
      // - If they left it blank AND we are editing, keep existing PIN unchanged.
      // - If creating a new shop with blank PIN, default to "1234" (hashed).
      let pinToStore;
      if (pin && /^\d+$/.test(pin)) {
        pinToStore = await hashPin(pin);
      } else if (isNew) {
        pinToStore = await hashPin("1234");
      } else {
        pinToStore = shop.admin_pin; // unchanged
      }
      const patch = { name, slug, admin_pin: pinToStore, plan, payment_status: pay, start_date: start, expiry_date: expiry || null, notes };
      if (isNew) {
        const created = await createShop({ slug, name, tagline: null, admin_pin: pinToStore });
        await updateShop(created.id, patch);
        await bulkCreateServices(created.id, [
          { name: "Consultation", duration_minutes: 15, price: 0, sort_order: 1 },
          { name: "Standard", duration_minutes: 30, price: 500, sort_order: 2 },
        ]);
        toast.success("Shop created");
      } else {
        await updateShop(shop.id, patch);
        toast.success("Saved");
      }
      onClose();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-5" onClick={onClose}>
      <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} onClick={(e) => e.stopPropagation()} className="glass-strong rounded-2xl w-full max-w-lg p-6 md:p-8" data-testid="super-editor">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-numbers text-[10px] tracking-[0.4em] uppercase text-gold">{isNew ? "Create Shop" : "Edit Shop"}</div>
            <div className="font-heading text-xl mt-1">{name || "New Shop"}</div>
          </div>
          <button onClick={onClose} className="p-2 text-white/50 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="mt-6 space-y-4">
          <Row><Lbl>Name</Lbl><input className="input-minimal" value={name} onChange={(e) => setName(e.target.value)} data-testid="edit-name" /></Row>
          <Row><Lbl>Slug</Lbl><input className="input-minimal font-numbers" value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))} disabled={!isNew} data-testid="edit-slug" /></Row>
          <Row>
            <Lbl>{pinIsHashed ? "Reset Admin PIN (leave blank to keep)" : "Admin PIN"}</Lbl>
            <input
              className="input-minimal font-numbers"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
              placeholder={pinIsHashed ? "•••• (encrypted — type new PIN to reset)" : "1234"}
              data-testid="edit-pin"
            />
          </Row>
          <div className="grid grid-cols-3 gap-3">
            <SelectField label="Plan" value={plan} setValue={setPlan} options={PLANS} testid="edit-plan" />
            <SelectField label="Payment" value={pay} setValue={setPay} options={PAY_STATUS} testid="edit-payment" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Row><Lbl>Start Date</Lbl><input type="date" className="input-minimal font-numbers" value={start} onChange={(e) => setStart(e.target.value)} data-testid="edit-start" /></Row>
            <Row><Lbl>Expiry Date</Lbl><input type="date" className="input-minimal font-numbers" value={expiry} onChange={(e) => setExpiry(e.target.value)} data-testid="edit-expiry" /></Row>
          </div>
          <Row><Lbl>Internal Notes</Lbl><textarea className="input-minimal" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="edit-notes" /></Row>
        </div>
        <div className="mt-7 flex justify-end gap-3">
          <button onClick={onClose} className="btn-ghost text-sm">Cancel</button>
          <button onClick={save} disabled={saving || !name || !slug} className="btn-gold text-sm py-2.5 px-5" data-testid="super-save">{saving ? "Saving…" : "Save"}</button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function Row({ children }) { return <div>{children}</div>; }
function Lbl({ children }) { return <label className="font-numbers text-[10px] tracking-[0.3em] uppercase text-white/40">{children}</label>; }
function SelectField({ label, value, setValue, options, testid }) {
  return (
    <div>
      <Lbl>{label}</Lbl>
      <div className="mt-2 flex gap-1.5 flex-wrap" data-testid={testid}>
        {options.map((o) => (
          <button key={o} type="button" onClick={() => setValue(o)} className={`text-[10px] font-numbers tracking-[0.2em] uppercase px-2.5 py-1 rounded-full border ${value === o ? "border-gold text-gold bg-gold/5" : "border-white/15 text-white/60"}`}>{o}</button>
        ))}
      </div>
    </div>
  );
}
